import React, { createContext, useContext, useEffect, useState } from 'react';

const ImageAccess = createContext(null);

export function ImageAccessProvider({ resolveImage, getIdentity, children }) {
  return <ImageAccess.Provider value={{ resolveImage, getIdentity }}>{children}</ImageAccess.Provider>;
}

export function imageObjectKey(value) {
  if (!value || /^(data:|blob:)/i.test(value)) return null;
  let path = value;
  if (/^https?:\/\//i.test(value)) {
    const url = new URL(value);
    if (url.origin !== globalThis.location?.origin && !url.pathname.startsWith('/bayesprep-files/')) return null;
    if (url.pathname.startsWith('/images/')) path = url.pathname;
    else if (url.pathname === '/api/core/images/content') return url.searchParams.get('imageUrl');
    else if (url.pathname.startsWith('/bayesprep-files/')) path = url.pathname.slice('/bayesprep-files/'.length);
    else return null;
  }
  if (path.startsWith('/api/core/images/content?')) return new URL(path, 'http://local').searchParams.get('imageUrl');
  if (path.startsWith('/images/')) return path.slice('/images/'.length);
  // Local bundled assets and external images remain ordinary images.
  if (path.startsWith('/')) return null;
  return path.includes('/') ? path : null;
}

export function ProtectedImage({ src, ...props }) {
  const access = useContext(ImageAccess);
  const key = imageObjectKey(src);
  const identity = access?.getIdentity?.() || '';
  const [resolved, setResolved] = useState(null);
  const resolver = access?.resolveImage;
  useEffect(() => {
    setResolved(null);
    if (!key || !resolver || !identity) return;
    const controller = new AbortController();
    let objectUrl;
    let active = true;
    resolver(key, controller.signal).then(blob => {
      if (!active) return;
      objectUrl = URL.createObjectURL(blob);
      setResolved({ key, identity, url: objectUrl });
    }).catch(() => { /* A denied image must never fall back to a public storage URL. */ });
    return () => {
      active = false;
      controller.abort();
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [key, identity, resolver]);
  const source = key ? (resolved?.key === key && resolved?.identity === identity ? resolved.url : undefined) : src;
  return <img {...props} src={source} />;
}
